import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import {
    FaTimes,
    FaShoppingBag,
    FaBoxOpen,
    FaTicketAlt,
    FaFire,
    FaCreditCard,
    FaCog,
    FaReceipt,
    FaSignOutAlt,
    FaTwitter,
    FaInstagram,
    FaFacebook,
    FaDiscord,
} from "react-icons/fa";
import { TbNotes } from "react-icons/tb";
import { IStore } from "@/redux/defaultStore";
import { toggleSidebar } from "@/redux/meta/metaActions";
import useThirwebWallet from "@/hooks/useThirwebWallet";
import useOnLogout from "@/hooks/useOnLogout";
import "./DevourPlayMobileSidebar.scss";

interface MobileSidebarItem {
    id: string;
    label: string;
    icon: React.ComponentType<any>;
    path?: string;
    action?: () => void;
    section?: "gaming" | "commerce" | "utility";
}

const MOBILE_SIDEBAR_ITEMS: MobileSidebarItem[] = [
    // Commerce Section
    {
        id: "lootboxes",
        label: "LootBoxes",
        icon: FaBoxOpen,
        path: "/devourlootbox",
        section: "commerce",
    },
    {
        id: "loot-shop",
        label: "LootShop",
        icon: FaShoppingBag,
        path: "/lootshop",
        section: "commerce",
    },
    {
        id: "my-orders",
        label: "My Orders",
        icon: TbNotes,
        path: "/menu-orders",
        section: "commerce",
    },
    {
        id: "load-fuel",
        label: "Load Fuel",
        icon: FaFire,
        path: "/load-fuel",
        section: "commerce",
    },

    // Utility Section
    {
        id: "my-collectibles",
        label: "My Collectibles",
        icon: FaBoxOpen,
        path: "/collectibles",
        section: "utility",
    },
    {
        id: "devourgo-wallet",
        label: "DevourPlay Wallet",
        icon: FaCreditCard,
        section: "utility",
    },
    {
        id: "account",
        label: "Account",
        icon: FaCog,
        path: "/account",
        section: "utility",
    },
];

export default function DevourPlayMobileSidebar() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const sidebarOpen = useSelector((store: IStore) => store.metaStore.sidebarOpen);
    const currentUser = useSelector((store: IStore) => store.metaStore.currentUser);
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { onOpenThirdwebWallet } = useThirwebWallet();
    const { devourLogout } = useOnLogout();

    const handleClose = () => {
        dispatch(toggleSidebar(false));
    };

    const handleItemClick = (item: MobileSidebarItem) => {
        if (item.id === "devourgo-wallet") {
            onOpenThirdwebWallet();
            handleClose();
            return;
        }

        if (item.path) {
            navigate(item.path);
            handleClose();
        }
    };

    const handleLogout = async () => {
        handleClose();
        await devourLogout();
        navigate("/");
    };

    if (!sidebarOpen) {
        return null;
    }

    return (
        <>
            <div
                className="devourplay-mobile-sidebar__overlay"
                onClick={handleClose}
            />
            <div className="devourplay-mobile-sidebar">
                <div className="devourplay-mobile-sidebar__header">
                    <img
                        src="/images/devourplay-logo2.png"
                        alt="DevourPlay"
                        className="devourplay-mobile-sidebar__logo"
                    />
                    <button
                        className="devourplay-mobile-sidebar__close"
                        onClick={handleClose}
                        aria-label="Close sidebar"
                    >
                        <FaTimes />
                    </button>
                </div>

                <nav className="devourplay-mobile-sidebar__nav">
                    {MOBILE_SIDEBAR_ITEMS.map(item =>
                        <button
                            key={item.id}
                            className="devourplay-mobile-sidebar__item"
                            onClick={() => handleItemClick(item)}
                        >
                            <item.icon className="devourplay-mobile-sidebar__item-icon" />
                            <span className="devourplay-mobile-sidebar__item-label">{item.label}</span>
                        </button>)}

                    {/* Logout - only show when logged in */}
                    {fullToken &&
            <button
                className="devourplay-mobile-sidebar__item devourplay-mobile-sidebar__item--logout"
                onClick={handleLogout}
            >
                <FaSignOutAlt className="devourplay-mobile-sidebar__item-icon" />
                <span className="devourplay-mobile-sidebar__item-label">Logout</span>
            </button>
                    }
                </nav>

                {/* Social Icons */}
                <div className="devourplay-mobile-sidebar__socials">
                    <a
                        href="https://twitter.com/GoDevour"
                        className="devourplay-mobile-sidebar__social-link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FaTwitter />
                    </a>
                    <a
                        href="https://www.instagram.com/go_devour/"
                        className="devourplay-mobile-sidebar__social-link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FaInstagram />
                    </a>
                    <a
                        href="https://www.facebook.com/GoDevour"
                        className="devourplay-mobile-sidebar__social-link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FaFacebook />
                    </a>
                    <a
                        href="https://discord.gg/devourplay"
                        className="devourplay-mobile-sidebar__social-link"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <FaDiscord />
                    </a>
                </div>

            </div>
        </>
    );
}